import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

/**
 * TextField customizado que suporta menções de usuários com @
 * Quando o usuário digita @, abre um dropdown com lista de usuários
 */
const MentionTextField = ({
  value,
  onChange,
  usuarios = [],
  label,
  placeholder,
  rows = 3,
  ...otherProps
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [filterText, setFilterText] = useState("");
  const textFieldRef = useRef(null);

  // Detecta quando o usuário digita @
  const handleTextChange = (event) => {
    const newValue = event.target.value;
    const cursorPos = event.target.selectionStart;

    onChange(event);

    // Encontrar a palavra atual (do cursor até o último espaço ou início)
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      // Se não houver espaço após o @, mostrar dropdown
      if (!textAfterAt.includes(" ")) {
        setFilterText(textAfterAt.toLowerCase());
        setShowDropdown(true);
        setCursorPosition(cursorPos);
        return;
      }
    }

    setShowDropdown(false);
  };

  // Quando seleciona um usuário do dropdown
  const handleUserSelect = (usuario) => {
    const currentValue = value || "";
    const cursorPos = cursorPosition;

    // Encontrar a posição do @ antes do cursor
    const textBeforeCursor = currentValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      // Substituir do @ até o cursor pelo nome do usuário
      const nomeUsuario = usuario.nome.replace(/\s+/g, "");
      const textoBefore = currentValue.substring(0, lastAtIndex);
      const textoAfter = currentValue.substring(cursorPos);
      const novoTexto = `${textoBefore}@${nomeUsuario} ${textoAfter}`;

      // Criar evento sintético para onChange
      const syntheticEvent = {
        target: {
          name: otherProps.name,
          value: novoTexto,
        },
      };

      onChange(syntheticEvent);

      // Fechar dropdown
      setShowDropdown(false);

      // Focar no campo de texto novamente
      setTimeout(() => {
        if (textFieldRef.current) {
          const input = textFieldRef.current.querySelector("textarea");
          if (input) {
            input.focus();
            const newCursorPos = lastAtIndex + nomeUsuario.length + 2; // +2 para @ e espaço
            input.setSelectionRange(newCursorPos, newCursorPos);
          }
        }
      }, 0);
    }
  };

  // Filtrar usuários baseado no texto digitado após @
  const usuariosFiltrados = usuarios.filter((usuario) => {
    if (!usuario || !usuario.nome) return false;

    const nomeCompleto = usuario.nome.toLowerCase();
    const nomeSemEspacos = usuario.nome.toLowerCase().replace(/\s+/g, "");
    const partesNome = usuario.nome.toLowerCase().split(" ");

    return (
      nomeCompleto.includes(filterText) ||
      nomeSemEspacos.includes(filterText) ||
      partesNome.some((parte) => parte.startsWith(filterText))
    );
  });

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        textFieldRef.current &&
        !textFieldRef.current.contains(event.target) &&
        !event.target.closest(".mention-dropdown")
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box sx={{ position: "relative" }}>
      <Box ref={textFieldRef}>
        <TextField
          fullWidth
          multiline
          rows={rows}
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={handleTextChange}
          {...otherProps}
        />
      </Box>

      {showDropdown && usuariosFiltrados.length > 0 && (
        <Paper
          className="mention-dropdown"
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            mt: 0.5,
            maxHeight: 200,
            overflow: "auto",
            zIndex: 1400,
            boxShadow: 3,
          }}
        >
          <List dense>
            {usuariosFiltrados.map((usuario) => (
              <ListItem key={usuario.id} disablePadding>
                <ListItemButton onClick={() => handleUserSelect(usuario)}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        @{usuario.nome.replace(/\s+/g, "")}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {usuario.nome}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          💡 Digite @ para mencionar um usuário ({usuarios.length} disponíveis)
        </Typography>
      </Box>
    </Box>
  );
};

export default MentionTextField;
